import { DELIVERED_STATUS_ID, type Delivery } from '../interfaces/ILogistics.js';
import type { DeliveryOrderLineBody, DeliveryOrderSubmit } from '../apis/logistics.js';
import type { IDeliveryLineDraft } from './deliveryLines.js';
import type { IOrderSuggestion } from './orderProvenance.js';
import type { IOpenStock } from './orderForm.types.js';
import { sameUnit } from '../../logistics/index.js';
import { estimatedOnHand } from './onHand.js';
import { supplierPayloadOf, type IOrderSupplierChoice } from './suppliers.js';

export type SeededFields = {
  /** The order's own fields (destination, supplier, ETA, note) plus the merge estimate,
   *  by field name. */
  fields: Record<string, string>;
  /** The seeded amount of each material ROW, by draft key. Per line because a
   *  pick seeds every row it fills, not only the first: an order carrying
   *  three materials had two of its three amounts worked out in silence. */
  lines: Record<string, string>;
};

/**
 * What the SYSTEM put in each field.
 *
 * Suggestions only — never `initial`. An edit's values are the user's own saved
 * answers; marking those as suggestions would be a lie, and would paint the
 * whole form amber every time someone opens an existing order.
 */
export function computeSeeded(
  initial: Delivery | null,
  lines: IDeliveryLineDraft[],
  suggested: IOrderSuggestion,
  openStock: IOpenStock | undefined,
): SeededFields {
  const fields: Record<string, string> = {};
  const amounts: Record<string, string> = {};
  if (!initial) {
    // Only what the system WORKED OUT. Amber has to mean "we changed this" or
    // it means nothing — the same rule the stock dialog follows. A value the
    // form merely COPIED from what the material already holds is not an
    // inference, so it stays in normal ink.
    //
    // Deliberately absent:
    //   itemName        the material the user clicked
    //   unit            carried forward from the material's own stock
    //   requestedDate   today, a fact about when the request was raised
    //
    // The destination joined the list on 2026-09-15: with several materials on
    // the order it is no longer carried forward but WORKED OUT — the last truck
    // that carried this exact combination, or deliberately left blank — and a
    // suggestion the user is meant to check has to look like one. The supplier
    // joined on 2026-10-26 by the same reasoning: it is a truck fact suggested
    // by exactly the destination's rules (spec 2026-10-25 §13).
    //
    // Truthiness, not `!= null`: a suggestion is '' for a field it could not
    // infer. An empty string is the ABSENCE of a suggestion — painting it amber
    // claims we filled a field we left blank, and would put a "Suggested" note
    // on it the moment the user typed the value themselves.
    //
    // The LIVE evaluation: a set of materials that offers nothing for a field
    // marks nothing and notes nothing — "Suggested X" beside a blank the
    // system itself cleared would claim a suggestion that no longer stands.
    for (const line of lines) {
      const quantity = line.prefill?.quantity;
      if (quantity != null) amounts[line.key] = String(quantity);
    }
    if (suggested.destinationName) fields.destinationName = suggested.destinationName;
    if (suggested.dueDate) fields.dueDate = suggested.dueDate;
    if (suggested.note) fields.note = suggested.note;
    if (suggested.supplierId) fields.supplierId = suggested.supplierId;
  }
  // The merge's remaining count is derived from the running estimate, so it is
  // seeded the same way even on an edit — nobody supplied it.
  if (openStock) {
    fields.mergeRemaining = String(estimatedOnHand(openStock));
  }
  return { fields, lines: amounts };
}

/**
 * The arrival: this delivery is being MOVED into Delivered and has not landed
 * before. The one board move with consequences past the column — the server
 * stamps `deliveredAt` on the way in (and clears it again on the way out, so
 * an arrival stays reversible).
 *
 * All three clauses are load-bearing:
 *
 *   moved       the column actually changed. Without this, a card already
 *               sitting in Delivered would stamp an arrival on a save that
 *               only touched the note — a claim nobody made.
 *   into        Delivered specifically.
 *   not before  `deliveredAt` is the arrival FACT — a card that already
 *               landed and is only being re-filed is not arriving again.
 *
 * Never on a create: that path offers no column at all.
 */
export function computeArriving(initial: Delivery | null, status: string): boolean {
  return (
    initial != null &&
    status === DELIVERED_STATUS_ID &&
    status !== initial.status &&
    initial.deliveredAt == null
  );
}

// The merge is offered only on the arrival transition, and only while the
// running stock still has cover. A row that has already run out is a Renew,
// not a merge — there is nothing left to add the delivery to. And only in the
// stock's own unit: 20 kg added to a count in bags is not 20 bags.
export function computeCanMerge(
  arriving: boolean,
  openStock: IOpenStock | undefined,
  hasAmount: boolean,
  unit: string,
): boolean {
  return arriving && openStock != null && openStock.daysLeft > 0 && hasAmount && sameUnit(unit, openStock.unit);
}

export function computeMergedTotal(
  canMerge: boolean,
  remValid: boolean,
  remNum: number,
  qtyNum: number,
): number | null {
  return canMerge && remValid ? Math.round((remNum + qtyNum) * 10) / 10 : null;
}

export interface ISubmitInputs {
  /** The materials table, already shaped for the wire (`draftsToBody`). */
  lines: DeliveryOrderLineBody[];
  destinationName: string;
  note: string;
  requestedDate: string;
  answeredDate: string;
  dueDate: string;
  initial: Delivery | null;
  status: string;
  canMerge: boolean;
  mergeStock: boolean;
  remNum: number;
  /** The picked supplier and any draft saved with the order (`supplierPayloadOf`). */
  supplier: IOrderSupplierChoice;
}

/** Shapes the submit call: the body, plus the merge argument — omitted
 *  entirely (not passed as an explicit `undefined`) when there is no merge,
 *  since a two-arg call with `undefined` still counts as two args for any
 *  caller or assertion inspecting `mock.calls[0]`. */
export function buildSubmitPayload(
  inputs: ISubmitInputs,
): { payload: DeliveryOrderSubmit; merge: { remaining: number } | null } {
  const {
    lines, destinationName, note,
    requestedDate, answeredDate, dueDate, initial, status,
    canMerge, mergeStock, remNum, supplier,
  } = inputs;
  const payload: DeliveryOrderSubmit = {
    lines,
    destinationName: destinationName.trim(),
    deliveryOrder: note.trim() ? note.trim() : null,
    requestedDate: requestedDate === '' ? null : requestedDate,
    answeredDate: answeredDate === '' ? null : answeredDate,
    dueDate: dueDate === '' ? null : dueDate,
    /* The column the user picked — the only status key that exists. An
       arrival is not a separate write any more: moving into the Delivered
       system column IS how the server knows to stamp `deliveredAt`, and
       moving back out is how it knows to clear it. Sent only when the column
       actually changed; never on a create, where a new delivery starts in
       the first column and the server derives it. */
    ...(initial && status !== initial.status ? { status } : {}),
    ...supplierPayloadOf(supplier, initial),
  };
  const merge = canMerge && mergeStock ? { remaining: remNum } : null;
  return { payload, merge };
}
